import { nanoid } from '@blockworks/platform';
import {
    COLS_BY_BREAKPOINT,
    findTopVisualization,
    GetDashboardResponse,
    type LayoutConfig,
} from '@blockworks/platform/api/research/studio';

const ROW_HEIGHT = 18;

const makePreloadedModeLayouts = (
    layoutConfig?: LayoutConfig,
    visualizations?: GetDashboardResponse['data']['visualizations'],
) => {
    const lockedLayoutConfig: LayoutConfig = { lg: [], base: [] };
    const layoutItemByVizId: Record<string, string> = {};

    if (!visualizations) {
        console.error('No visualizations provided to makePreloadedModeLayouts');
        return lockedLayoutConfig;
    }

    /** separate featured and non-featured items */
    const unlockedItems = visualizations.filter(viz => viz.isFeatured);
    const lockedItems = visualizations.filter(viz => !viz.isFeatured);

    /** if there are no featured items, add the first non-featured item as a featured item */
    if (!unlockedItems.length && !!lockedItems.length) {
        const featuredFallback = lockedItems[0]!;

        if (!layoutConfig) {
            unlockedItems.push({ ...featuredFallback, isFeatured: true });
            lockedItems.shift();
        } else {
            const { topMostVisualization, index } = findTopVisualization(layoutConfig, lockedItems, 'id');
            unlockedItems.push({ ...(topMostVisualization ?? featuredFallback), isFeatured: true });
            lockedItems.splice(index, 1);
        }
    }

    /** track the current row for placement */
    let currentY = 0;

    const addItemToLayout = (
        viz: GetDashboardResponse['data']['visualizations'][number],
        w: number,
        h: number,
        x: number,
        y: number,
    ) => {
        const i = nanoid();
        layoutItemByVizId[viz.id] = i;

        lockedLayoutConfig.lg.push({
            i,
            w,
            h,
            x,
            y,
            data: {
                id: viz.id,
                type: viz.type,
                isFeatured: viz.isFeatured ?? false,
            },
        });
    };

    // Place featured items
    unlockedItems.forEach((viz, index) => {
        if (unlockedItems.length === 1 || (unlockedItems.length % 2 !== 0 && index === 0)) {
            /** single featured item or first of an odd number of featured items */
            addItemToLayout(viz, COLS_BY_BREAKPOINT.lg, ROW_HEIGHT, 0, currentY);
            currentY += ROW_HEIGHT; /** move to the next row */
        } else {
            /** even number of featured items or remaining items in an odd set */
            const x = index % 2 === 0 ? 0 : 3; /** alternate between left and right columns */
            if (x === 0 && index > 0) currentY += ROW_HEIGHT; /** move to a new row after every 2 items */
            addItemToLayout(viz, 3, ROW_HEIGHT, x, currentY);
        }
    });

    /** move to the next row if the last row was partially filled */
    if (unlockedItems.length > 1 && unlockedItems.length % 2 !== 0) {
        currentY += ROW_HEIGHT;
    }

    /** place non-featured items */
    lockedItems.forEach((viz, index) => {
        const x = index % 2 === 0 ? 0 : 3; /** alternate between left and right columns */
        if (x === 0 && index > 0) currentY += ROW_HEIGHT; /** move to a new row after every 2 items */
        addItemToLayout(viz, 3, ROW_HEIGHT, x, currentY);
    });

    /** base config for mobile; create a single-column layout */
    [...unlockedItems, ...lockedItems].forEach((viz, index) => {
        const i = layoutItemByVizId[viz.id];

        if (!i) return;

        lockedLayoutConfig.base.push({
            i,
            w: COLS_BY_BREAKPOINT.base,
            h: ROW_HEIGHT,
            x: 0,
            y: index * ROW_HEIGHT /** stack items vertically */,
            data: {
                id: viz.id,
                type: viz.type,
                isFeatured: viz.isFeatured ?? false,
            },
        });
    });

    return lockedLayoutConfig;
};

export { makePreloadedModeLayouts };
